import { FC, useEffect, useMemo, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
} from '@features/admin/reports/reportData';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  ReportGroupBy,
  ReportPeriod,
  TReportOldAffiliateSaleNumberData,
} from '@src/types/reports';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { buildAffiliateCommissionsPayload } from '@src/utils/buildCommonPayload';
import {
  exportOldAffiliateSaleNumber,
  getOldAffiliateSaleNumber,
} from '@src/services/reports';
import { toast } from 'react-toastify';
import { downloadFile } from '@src/lib/utils';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    payload: TReportOldAffiliateSaleNumberData;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col items-center min-w-[135px] gap-y-1"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#B6D72C] text-xl font-semibold">
          {payload[0]?.value.toLocaleString()}
        </p>
        <p className="text-[#999999] text-[10px]">
          {payload[0]?.payload?.date}
        </p>
        <p className="text-white text-xs">
          Sales: {payload[0]?.value.toLocaleString()}
        </p>
      </div>
    );
  }

  return null;
};

export const OldAffiliateSaleNumber: FC = () => {
  // Data state
  const [data, setData] = useState<TReportOldAffiliateSaleNumberData[]>([]);

  // Use custom hooks for filter management
  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.THIS_YEAR,
    defaultGroupBy: ReportGroupBy.MONTH,
    enableComparisons: true,
  });

  const reportRows = useMemo(() => data.filter((item) => !item.isTotal), [data]);
  const totalRow = useMemo(() => data.find((item) => item.isTotal) ?? null, [data]);
  const tableSource = useMemo(
    () => [...reportRows, ...(totalRow ? [totalRow] : [])],
    [reportRows, totalRow]
  );

  // Use custom hook for table columns - single metric
  const { columns, tableData } =
    useReportTableColumns<TReportOldAffiliateSaleNumberData>({
      data: tableSource,
      getAccessField: (item) => item.date,
      getMainDataField: (item) => item.sales || 0,
      customColumns: [
        {
          title: 'SALES',
          className: 'text-right',
          render: (item) => (
            <p className="text-white">
              {item?.sales?.toLocaleString() ?? 0}
            </p>
          ),
        },
        ...(filters.compareToPreviousPeriod
          ? [
              {
                title: 'SALES (PREVIOUS PERIOD)',
                className: 'text-right',
                render: (item?: TReportOldAffiliateSaleNumberData) => (
                  <p className="text-[#FF6B6B]">
                    {(item?.sales_previous ?? 0).toLocaleString()}
                  </p>
                ),
              },
            ]
          : []),
        ...(filters.compareToSamePeriodPreviousYear
          ? [
              {
                title: 'SALES (PREVIOUS YEAR)',
                className: 'text-right',
                render: (item?: TReportOldAffiliateSaleNumberData) => (
                  <p className="text-[#4ECDC4]">
                    {(item?.sales_previous_year ?? 0).toLocaleString()}
                  </p>
                ),
              },
            ]
          : []),
      ],
      includeTotalsRow: false,
    });

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const payload = buildAffiliateCommissionsPayload(filters);
      const response = await getOldAffiliateSaleNumber(payload);
      setData(response);
      toast.success('Report generated successfully!');
    } catch (error) {
      toast.error('Error generating report. Please try again.');
      console.error('Error fetching report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const payload = buildAffiliateCommissionsPayload(filters);
      const response = await exportOldAffiliateSaleNumber(payload);
      downloadFile(response.data, 'old-affiliate-sale-number');
      toast.success('Report exported successfully!');
    } catch (error) {
      toast.error('Error exporting report. Please try again.');
      console.error('Error exporting report:', error);
    }
  };

  useEffect(() => {
    handleRunReport();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Old Affiliate Sale Number Report
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={reportRows}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend iconType="line" wrapperStyle={{ fontSize: '14px' }} />
              <Line
                activeDot={{ r: 6 }}
                dataKey="sales"
                dot={{ fill: '#B6D72C', r: 4 }}
                name="Sales"
                stroke="#B6D72C"
                strokeWidth={2}
                type="monotone"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          <div className="flex justify-end">
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              variant="outline"
              onClick={handleExportReport}
            >
              Download CSV
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
